import { escapeXml, formatDateXml, buildXmlPayload, sendXmlRequest } from './core';

// ============ DOCUMENT ENTRY CONFIGURATION ============
const DOCUMENT_ENTRY_TYPES = {
  'PODOCUMENTENTRY': {
    parentKeyField: 'DOCUMENTID', lineNoField: 'LINE_NO', updateFunction: 'update_potransaction',
    updateKeyAttribute: 'key', updateKeyPrefix: '', containerTag: 'updatepotransitems',
    itemTag: 'updatepotransitem', lineNoAttribute: 'line_num', lineNoOffset: 1,
    dateFields: ['DATECREATED', 'DATEDUE'],
    excludeFields: ['RECORDNO', 'WHENCREATED', 'WHENMODIFIED', 'CREATEDBY', 'MODIFIEDBY', 'DOCUMENTID', 'LINE_NO'],
    isDestructive: false,
  },
  'PODOCUMENT': {
    parentKeyField: 'DOCUMENTID', keyAliases: ['DOCUMENTID', 'DOCID'], lineNoField: null, updateFunction: 'update_potransaction',
    updateKeyAttribute: 'key', updateKeyPrefix: '', containerTag: null, itemTag: null,
    lineNoAttribute: null, lineNoOffset: 0, dateFields: ['DATECREATED', 'DATEDUE'],
    fieldNameMap: { WHENCREATED: 'datecreated', WHENDUE: 'datedue' },
    excludeFields: ['RECORDNO', 'WHENMODIFIED', 'CREATEDBY', 'MODIFIEDBY', 'DOCUMENTID', 'DOCID'],
    isDestructive: false,
  },
  'SODOCUMENTENTRY': {
    parentKeyField: 'DOCUMENTID', lineNoField: 'LINE_NO', updateFunction: 'update_sotransaction',
    updateKeyAttribute: 'key', updateKeyPrefix: '', containerTag: 'updatesotransitems',
    itemTag: 'updatesotransitem', lineNoAttribute: 'line_num', lineNoOffset: 1,
    dateFields: ['DATECREATED', 'DATEDUE'],
    excludeFields: ['RECORDNO', 'WHENCREATED', 'WHENMODIFIED', 'CREATEDBY', 'MODIFIEDBY', 'DOCUMENTID', 'LINE_NO'],
    isDestructive: false,
  },
  'SODOCUMENT': {
    parentKeyField: 'DOCUMENTID', lineNoField: null, updateFunction: 'update_sotransaction',
    updateKeyAttribute: 'key', updateKeyPrefix: '', containerTag: null, itemTag: null,
    lineNoAttribute: null, lineNoOffset: 0, dateFields: ['DATECREATED', 'DATEDUE'],
    excludeFields: ['RECORDNO', 'WHENCREATED', 'WHENMODIFIED', 'CREATEDBY', 'MODIFIEDBY', 'DOCUMENTID'],
    isDestructive: false,
  },
};

const DOCUMENT_PARENT_FIELDS = {
  'PODOCUMENTENTRY': ['DOCUMENTID'],
  'PODOCUMENT': ['DOCUMENTID'],
  'SODOCUMENTENTRY': ['DOCUMENTID'],
  'SODOCUMENT': ['DOCUMENTID'],
};

// ============ HELPERS ============
const splitHeaderAndLineFields = (headers, cfg) => {
  if (!cfg.lineNoField) {
    return { headerFields: headers.filter(h => !cfg.excludeFields.includes(h.toUpperCase())), lineFields: [] };
  }
  const lineNoIdx = headers.findIndex(h => h.toUpperCase() === cfg.lineNoField);
  if (lineNoIdx === -1) return { headerFields: [], lineFields: headers };
  
  const headerFields = [];
  const lineFields = [];
  headers.forEach((h, idx) => {
    const upper = h.toUpperCase();
    if (cfg.excludeFields.includes(upper)) return;
    if (idx < lineNoIdx) headerFields.push(h);
    else if (upper !== cfg.lineNoField) lineFields.push(h);
  });
  return { headerFields, lineFields };
};

const buildDocFieldXml = (fieldName, value, cfg) => {
  const upper = fieldName.toUpperCase();
  // Translate a source/query header to its target XML element (e.g. WHENCREATED -> datecreated).
  const target = (cfg.fieldNameMap && cfg.fieldNameMap[upper]) || fieldName.toLowerCase();
  const targetUpper = target.toUpperCase();
  if (cfg.dateFields && (cfg.dateFields.includes(upper) || cfg.dateFields.includes(targetUpper))) {
    return formatDateXml(value, target);
  }
  return `<${target}>${escapeXml(String(value))}</${target}>`;
};

// Resolve a record's key value from whichever alias column is present (e.g. DOCUMENTID or DOCID).
const getDocKey = (rec, cfg) => {
  const aliases = cfg.keyAliases || [cfg.parentKeyField];
  for (const a of aliases) {
    const v = rec[a];
    if (v !== undefined && v !== '' && v !== null) return v;
  }
  return undefined;
};

// ============ DETECTION ============
export const isDocumentEntryType = (objectName) => {
  return Object.keys(DOCUMENT_ENTRY_TYPES).includes(objectName?.toUpperCase());
};

export const getDocumentEntryConfig = (objectName) => {
  return DOCUMENT_ENTRY_TYPES[objectName?.toUpperCase()] || null;
};

// Return the actual header present for the key (respects DOCUMENTID/DOCID aliases), or null.
export const resolveDocumentKeyHeader = (objectName, headers) => {
  const cfg = DOCUMENT_ENTRY_TYPES[objectName?.toUpperCase()];
  if (!cfg || !headers) return null;
  const aliases = cfg.keyAliases || [cfg.parentKeyField];
  const upper = headers.map(h => String(h).toUpperCase().trim());
  for (const a of aliases) if (upper.includes(a)) return a;
  return null;
};

// ============ FUNCTIONS ============
export const getDocumentUpdateSummary = (entryObjectName, records, headers = null) => {
  const cfg = DOCUMENT_ENTRY_TYPES[entryObjectName?.toUpperCase()];
  if (!cfg) return null;
  
  const grouped = {};
  const missingDocId = [];
  const missingLineNo = [];
  
  records.forEach((rec, idx) => {
    const docId = getDocKey(rec, cfg);
    if (!docId) { missingDocId.push(idx + 2); return; }
    if (cfg.lineNoField) {
      const lineNo = rec[cfg.lineNoField];
      if (lineNo === undefined || lineNo === '' || lineNo === null) { missingLineNo.push(idx + 2); return; }
    }
    if (!grouped[docId]) grouped[docId] = 0;
    grouped[docId]++;
  });
  
  let headerFieldCount = 0;
  if (headers && headers.length > 0) {
    const { headerFields } = splitHeaderAndLineFields(headers, cfg);
    headerFieldCount = headerFields.length;
  }
  
  return {
    config: cfg, entryTypeName: entryObjectName,
    documents: Object.entries(grouped).map(([id, count]) => ({ id, lineCount: count })),
    missingDocIdRows: missingDocId, missingLineNoRows: missingLineNo,
    totalLines: records.length, totalDocuments: Object.keys(grouped).length,
    isDestructive: cfg.isDestructive, isHeaderOnly: !cfg.lineNoField, headerFieldCount,
    keyHeader: resolveDocumentKeyHeader(entryObjectName, headers) || cfg.parentKeyField
  };
};

export const buildDocumentPreviewXml = (entryObjectName, records, headers = null) => {
  const cfg = DOCUMENT_ENTRY_TYPES[entryObjectName?.toUpperCase()];
  if (!cfg) return '';
  
  const grouped = {};
  records.forEach(rec => {
    const docId = getDocKey(rec, cfg);
    if (!docId) return;
    if (!grouped[docId]) grouped[docId] = [];
    grouped[docId].push(rec);
  });
  
  const docIds = Object.keys(grouped);
  if (docIds.length === 0) return `<!-- No valid records with ${cfg.parentKeyField} -->`;
  
  const firstDocId = docIds[0];
  const firstDocLines = grouped[firstDocId];
  const docKey = `${cfg.updateKeyPrefix}${firstDocId}`;
  const firstLine = firstDocLines[0];
  
  const { headerFields, lineFields } = headers 
    ? splitHeaderAndLineFields(headers, cfg) 
    : { headerFields: [], lineFields: [] };
  
  let headerXml = '';
  if (headerFields.length > 0) {
    headerXml = headerFields
      .filter(h => {
        const val = firstLine[h] || firstLine[h.toUpperCase()];
        return val !== '' && val !== undefined && val !== null;
      })
      .map(h => {
        const val = firstLine[h] || firstLine[h.toUpperCase()];
        return `\n      ${buildDocFieldXml(h, val, cfg)}`;
      })
      .join('');
  }
  
  let linesSection = '';
  if (cfg.lineNoField && cfg.containerTag) {
    const sampleLines = firstDocLines.slice(0, 2);
    const linesXml = sampleLines.map(line => {
      const lineNo = (parseInt(line[cfg.lineNoField], 10) || 0) + cfg.lineNoOffset;
      const fieldsToUse = lineFields.length > 0 ? lineFields : Object.keys(line);
      const lineFieldsXml = fieldsToUse
        .filter(k => {
          if (cfg.excludeFields.includes(k.toUpperCase())) return false;
          const val = line[k];
          return val !== '' && val !== undefined && val !== null;
        })
        .map(k => `<${k.toLowerCase()}>${escapeXml(String(line[k]))}</${k.toLowerCase()}>`)
        .join('\n          ');
      return `<${cfg.itemTag} ${cfg.lineNoAttribute}="${lineNo}">\n          ${lineFieldsXml}\n        </${cfg.itemTag}>`;
    }).join('\n        ');
    
    const moreEntriesNote = firstDocLines.length > 2 ? `\n        <!-- ... and ${firstDocLines.length - 2} more lines ... -->` : '';
    linesSection = `\n    <${cfg.containerTag}>\n        ${linesXml}${moreEntriesNote}\n    </${cfg.containerTag}>`;
  }
  
  const moreDocsNote = docIds.length > 1 ? `\n\n<!-- ... and ${docIds.length - 1} more document(s) will be updated ... -->` : '';
  
  return `<function controlid="${escapeXml(firstDocId)}">
  <${cfg.updateFunction} ${cfg.updateKeyAttribute}="${escapeXml(docKey)}">${headerXml}${linesSection}
  </${cfg.updateFunction}>
</function>${moreDocsNote}`;
};

export const writeDocumentEntries = async (config, entryObjectName, records, headers = null) => {
  const cfg = DOCUMENT_ENTRY_TYPES[entryObjectName?.toUpperCase()];
  if (!cfg) throw new Error(`Unknown document entry type: ${entryObjectName}`);
  
  const missingDocId = records.filter(r => !getDocKey(r, cfg));
  if (missingDocId.length > 0) throw new Error(`${missingDocId.length} record(s) missing ${cfg.parentKeyField}.`);
  
  if (cfg.lineNoField) {
    const missingLineNo = records.filter(r => r[cfg.lineNoField] === undefined || r[cfg.lineNoField] === '' || r[cfg.lineNoField] === null);
    if (missingLineNo.length > 0) throw new Error(`${missingLineNo.length} record(s) missing ${cfg.lineNoField}.`);
  }
  
  const grouped = {};
  records.forEach(rec => {
    const docId = getDocKey(rec, cfg);
    if (!grouped[docId]) grouped[docId] = [];
    grouped[docId].push(rec);
  });
  
  const { headerFields, lineFields } = headers 
    ? splitHeaderAndLineFields(headers, cfg) 
    : { headerFields: [], lineFields: [] };
  
  const functions = Object.entries(grouped).map(([docId, lines]) => {
    const docKey = `${cfg.updateKeyPrefix}${docId}`;
    
    let headerXml = '';
    if (headerFields.length > 0) {
      const firstRow = lines[0];
      headerXml = headerFields
        .filter(h => {
          const val = firstRow[h] || firstRow[h.toUpperCase()];
          return val !== '' && val !== undefined && val !== null;
        })
        .map(h => {
          const val = firstRow[h] || firstRow[h.toUpperCase()];
          return buildDocFieldXml(h, val, cfg);
        })
        .join('');
    }
    
    let linesXml = '';
    if (cfg.lineNoField && cfg.containerTag) {
      const linesContent = lines.map(line => {
        const lineNo = (parseInt(line[cfg.lineNoField], 10) || 0) + cfg.lineNoOffset;
        const fieldsToUse = lineFields.length > 0 ? lineFields : Object.keys(line);
        const lineFieldsXml = fieldsToUse
          .filter(k => {
            if (cfg.excludeFields.includes(k.toUpperCase())) return false;
            const val = line[k];
            return val !== '' && val !== undefined && val !== null;
          })
          .map(k => `<${k.toLowerCase()}>${escapeXml(String(line[k]))}</${k.toLowerCase()}>`)
          .join('');
        return `<${cfg.itemTag} ${cfg.lineNoAttribute}="${lineNo}">${lineFieldsXml}</${cfg.itemTag}>`;
      }).join('');
      linesXml = `<${cfg.containerTag}>${linesContent}</${cfg.containerTag}>`;
    }
    
    return `<function controlid="${escapeXml(String(docId))}"><${cfg.updateFunction} ${cfg.updateKeyAttribute}="${escapeXml(docKey)}">${headerXml}${linesXml}</${cfg.updateFunction}></function>`;
  }).join('');
  
  const xmlPayload = buildXmlPayload(config, functions);
  const result = await sendXmlRequest(config, entryObjectName, xmlPayload, 'DOCUMENT UPDATE');
  return result.results;
};